import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AppConfirmation, AppConfirmationService } from '../../services/confirmation.service';

/**
 * Drop-in replacement for PrimeNG's p-confirmDialog.
 *
 * MIGRATION: only the tag name changes.
 *   <p-confirmDialog />
 *   becomes
 *   <app-confirm-dialog />
 *
 * No inputs are used in your current usage, so nothing else changes.
 * Place this once near the root of your component tree (same place
 * <p-confirmDialog /> was), alongside AppConfirmationService.confirm({...})
 * calls anywhere else in the app.
 */
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (confirmation) {
      <div class="app-confirm-overlay" (click)="onOverlayClick()">
        <div class="app-confirm-dialog" (click)="$event.stopPropagation()">
          @if (confirmation.header) {
            <div class="app-confirm-header">{{ confirmation.header }}</div>
          }
          <div class="app-confirm-message">{{ confirmation.message }}</div>
          <div class="app-confirm-footer">
            <button
              type="button"
              class="app-btn app-btn-secondary"
              (click)="onReject()"
            >
              {{ confirmation.rejectLabel || 'Cancel' }}
            </button>
            <button
              type="button"
              class="app-btn"
              (click)="onAccept()"
            >
              {{ confirmation.acceptLabel || 'Yes' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class AppConfirmDialogComponent implements OnInit, OnDestroy {
  confirmation: AppConfirmation | null = null;
  private sub?: Subscription;

  constructor(private readonly confirmationService: AppConfirmationService) {}

  ngOnInit(): void {
    this.sub = this.confirmationService.activeConfirmation$.subscribe((c) => {
      this.confirmation = c;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onAccept(): void {
    this.confirmationService.onAccept();
  }

  onReject(): void {
    this.confirmationService.onReject();
  }

  /** Clicking the dimmed backdrop behaves like Reject/Cancel — matches PrimeNG's default dismissable behavior */
  onOverlayClick(): void {
    this.onReject();
  }
}